
import React, { useState, useCallback, useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Avatar,
  useMediaQuery,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  MoreVert,
  Add,
  Star,
  Email,
  Refresh,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import AddChannelModal from "../../components/AddChannelModal";
import ZohoMailModal from "../../components/ZohoMailModal";
import Title from "../../components/Title";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from '../../utils/formatSerializedId';
import {
  FACEBOOK_OAUTH_SCOPES,
  INSTAGRAM_OAUTH_SCOPES,
  logFacebookOAuthDebug,
} from "../../utils/facebookOAuthDebug";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  canUserAccessConnectionsPage,
  canUserAccessConnectionChannel,
  getConnectionsPageRole,
} from "../../utils/userPermissions";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";
import IntegrationConnectionCard from "../../components/IntegrationConnectionCard";
import QueueIntegrationModal from "../../components/QueueIntegrationModal";
import WebSearchIntegrationModal from "../../components/WebSearchIntegrationModal";
import ChatAgentIntegrationModal from "../../components/ChatAgentIntegrationModal";
import PromptModal from "../../components/PromptModal";
import {
  getCatalogByType,
  hasAnyIntegrationAccess,
} from "../../utils/integrationCatalog";

const CHANNEL_META = {
  whatsapp: {
    label: "WhatsApp",
    color: "#25D366",
    bg: "rgba(37, 211, 102, 0.12)",
    Icon: WhatsApp,
  },
  facebook: {
    label: "Facebook",
    color: "#1877F2",
    bg: "rgba(24, 119, 242, 0.12)",
    Icon: Facebook,
  },
  instagram: {
    label: "Instagram",
    color: "#E1306C",
    bg: "rgba(225, 48, 108, 0.12)",
    Icon: Instagram,
  },
  email: {
    label: "Zoho Mail",
    color: "#E42527",
    bg: "rgba(228, 37, 39, 0.12)",
    Icon: Email,
  },
};

const STATUS_META = {
  CONNECTED: { color: "#059669", bg: "rgba(5, 150, 105, 0.12)" },
  DISCONNECTED: { color: "#DC2626", bg: "rgba(220, 38, 38, 0.12)" },
  OPENING: { color: "#D97706", bg: "rgba(217, 119, 6, 0.12)" },
  qrcode: { color: "#2563EB", bg: "rgba(37, 99, 235, 0.12)" },
  PAIRING: { color: "#D97706", bg: "rgba(217, 119, 6, 0.12)" },
  TIMEOUT: { color: "#DC2626", bg: "rgba(220, 38, 38, 0.12)" },
};

const useStyles = makeStyles((theme) => ({
  connectionsHeader: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: "4px 4px 12px 4px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: theme.spacing(1),
      padding: "0 0 8px 0",
    },
  },
  pageHeader: {
    flex: "1 1 auto",
    minWidth: 0,
    marginBottom: 0,
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 auto",
      minWidth: 0,
      "& .MuiTypography-h5": {
        fontSize: "1.125rem",
        marginBottom: "0 !important",
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  },
  headerActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "auto",
      flex: "0 0 auto",
      flexWrap: "nowrap",
      gap: theme.spacing(0.5),
    },
  },
  headerActionItem: {
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      width: "auto",
      "& > button": {
        width: "auto",
      },
    },
  },
  headerIconBtn: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75),
  },
  headerIconBtnPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  headerBtn: {
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      fontSize: "0.8125rem",
      padding: theme.spacing(1, 1.5),
      minHeight: 40,
      "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.75),
        "& > *:nth-of-type(1)": {
          fontSize: 18,
        },
      },
    },
    [theme.breakpoints.down(360)]: {
      "& .MuiButton-startIcon": {
        display: "none",
      },
    },
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: 4,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  filtersScroll: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
      marginBottom: theme.spacing(1.5),
      overflowX: "auto",
      overflowY: "hidden",
      flexWrap: "nowrap",
      paddingBottom: 2,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  filterSeparator: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      flex: "0 0 1px",
      alignSelf: "stretch",
      minHeight: 24,
      margin: theme.spacing(0, 0.25),
      backgroundColor: theme.palette.divider,
    },
  },
  filterChip: {
    fontWeight: 500,
    borderRadius: 20,
    height: 32,
    [theme.breakpoints.down("sm")]: {
      height: 28,
      fontSize: "0.75rem",
      flex: "0 0 auto",
    },
  },
  filterChipActive: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "#fff !important",
  },
  integrationsSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5),
      borderRadius: 12,
    },
  },
  integrationsSectionHeader: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.25),
    },
  },
  integrationsSectionTitle: {
    fontWeight: 700,
    fontSize: "1.0625rem",
    lineHeight: 1.3,
  },
  integrationsSectionSubtitle: {
    marginTop: 4,
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
  gridContainer: {
    flex: 1,
    overflowY: "auto",
    paddingBottom: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  connectionCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    border: theme.mode === "light"
      ? "1px solid rgba(15, 23, 42, 0.08)"
      : "1px solid rgba(148, 163, 184, 0.12)",
    boxShadow: theme.mode === "light"
      ? "0 1px 3px rgba(15, 23, 42, 0.06)"
      : "0 2px 8px rgba(0, 0, 0, 0.2)",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: theme.mode === "light"
        ? "0 8px 24px rgba(15, 23, 42, 0.1)"
        : "0 8px 24px rgba(0, 0, 0, 0.35)",
      transform: "translateY(-2px)",
    },
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  channelAvatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: "0.9375rem",
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  cardId: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  statusBadge: {
    fontWeight: 600,
    fontSize: "0.6875rem",
    letterSpacing: "0.04em",
    height: 24,
    borderRadius: 12,
    marginBottom: theme.spacing(1.5),
  },
  identifier: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
    marginBottom: theme.spacing(1),
    minHeight: 20,
  },
  defaultChip: {
    height: 22,
    fontSize: "0.6875rem",
    marginBottom: theme.spacing(1),
    backgroundColor: "rgba(5, 150, 105, 0.1)",
    color: "#059669",
  },
  cardActions: {
    marginTop: "auto",
    paddingTop: theme.spacing(1.5),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    alignItems: "center",
  },
  cardFooter: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: theme.mode === "light"
      ? "1px solid rgba(15, 23, 42, 0.06)"
      : "1px solid rgba(148, 163, 184, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerDate: {
    fontSize: "0.6875rem",
    color: theme.palette.text.secondary,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  importCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
  },
  buttonProgress: {
    color: green[500],
  },
  skeletonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
  },
  skeletonCard: {
    height: 220,
    borderRadius: 16,
    backgroundColor: theme.mode === "light"
      ? "rgba(15, 23, 42, 0.04)"
      : "rgba(148, 163, 184, 0.08)",
  },
  integrationLogo: {
    width: 44,
    height: 44,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
    border:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const getChannelMeta = (channel) =>
  CHANNEL_META[channel] || {
    label: channel || "Canal",
    color: "#64748B",
    bg: "rgba(100, 116, 139, 0.12)",
    Icon: Email,
  };

const getStatusMeta = (status) =>
  STATUS_META[status] || STATUS_META.DISCONNECTED;

const getConnectionIdentifier = (whatsApp) => {
  if (whatsApp.channel === "email") {
    return whatsApp.emailAddress || whatsApp.number || "—";
  }
  if (whatsApp.number && whatsApp.channel === "whatsapp") {
    return formatSerializedId(whatsApp.number);
  }
  if (whatsApp.number) return whatsApp.number;
  if (whatsApp.status === "DISCONNECTED") {
    return i18n.t("connections.card.checkSettings");
  }
  return "—";
};

const sortConnections = (list, sortMode) => {
  const items = [...list];

  switch (sortMode) {
    case "name":
      return items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "type":
      return items.sort((a, b) => {
        const byChannel = (a.channel || "").localeCompare(b.channel || "");
        return byChannel !== 0 ? byChannel : (a.name || "").localeCompare(b.name || "");
      });
    case "status": {
      const order = { CONNECTED: 0, PAIRING: 1, OPENING: 2, qrcode: 3, TIMEOUT: 4, DISCONNECTED: 5 };
      return items.sort(
        (a, b) => (order[a.status] ?? 99) - (order[b.status] ?? 99)
      );
    }
    case "default":
    default:
      return items.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }
};

const getItemTypeLabel = (item) => {
  if (item.kind === "channel") {
    return getChannelMeta(item.data.channel).label;
  }
  const catalog = getCatalogByType(item.type);
  return catalog ? i18n.t(catalog.labelKey) : item.type;
};

const sortConnectionItems = (items, sortMode) => {
  const list = [...items];

  switch (sortMode) {
    case "name":
      return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "type":
      return list.sort((a, b) => {
        const byType = getItemTypeLabel(a).localeCompare(getItemTypeLabel(b));
        return byType !== 0 ? byType : (a.name || "").localeCompare(b.name || "");
      });
    case "status": {
      const order = {
        CONNECTED: 0,
        ACTIVE: 0,
        PAIRING: 1,
        OPENING: 2,
        qrcode: 3,
        TIMEOUT: 4,
        DISCONNECTED: 5,
      };
      return list.sort(
        (a, b) => (order[a.status] ?? 99) - (order[b.status] ?? 99)
      );
    }
    case "default":
    default:
      return list.sort((a, b) => {
        if (a.kind !== b.kind) {
          if (a.kind === "channel" && a.data?.isDefault) return -1;
          if (b.kind === "channel" && b.data?.isDefault) return 1;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
  }
};

const mapIntegrationCardItem = (item) => {
  if (item.kind === "integration") {
    const integration = item.data;
    let subtitle = null;

    if (integration.type === "tavily") {
      try {
        const config = JSON.parse(integration.jsonContent || "{}");
        const count = Array.isArray(config.promptIds) ? config.promptIds.length : 0;
        subtitle = i18n.t("connections.integrationCard.tavilyPrompts", {
          count,
          defaultValue: `${count} Open AI con búsqueda web`,
        });
      } catch {
        subtitle = i18n.t("connections.integrationCard.tavilyConfigured", {
          defaultValue: "Búsqueda web Tavily",
        });
      }
    }

    if (integration.type === "chat_agent") {
      try {
        const config = JSON.parse(integration.jsonContent || "{}");
        const count = Array.isArray(config.promptIds) ? config.promptIds.length : 0;
        subtitle = i18n.t("connections.integrationCard.chatAgentPrompts", {
          count,
          defaultValue: `${count} Open AI para análisis de chats`,
        });
      } catch {
        subtitle = i18n.t("connections.integrationCard.chatAgentConfigured", {
          defaultValue: "Agente IA de chats conectado",
        });
      }
    }

    return {
      id: integration.id,
      menuId: item.id,
      name: integration.name,
      integrationType: integration.type,
      subtitle,
      updatedAt: integration.updatedAt
        ? format(parseISO(integration.updatedAt), "dd/MM/yyyy HH:mm")
        : null,
      kind: item.kind,
      raw: integration,
    };
  }

  const prompt = item.data;
  const subtitleParts = [prompt.model];
  if (prompt.webSearchEnabled) {
    subtitleParts.push(
      i18n.t("connections.integrationCard.webSearch", {
        defaultValue: "Búsqueda web",
      })
    );
  }
  return {
    id: prompt.id,
    menuId: item.id,
    name: prompt.name,
    integrationType: "openai",
    subtitle: subtitleParts.join(" · "),
    webSearchEnabled: Boolean(prompt.webSearchEnabled),
    updatedAt: prompt.updatedAt
      ? format(parseISO(prompt.updatedAt), "dd/MM/yyyy HH:mm")
      : null,
    kind: item.kind,
    raw: prompt,
  };
};

const ConnectionCard = ({
  whatsApp,
  classes,
  user,
  onEdit,
  onDelete,
  renderActionButtons,
  renderStatusIcon,
  menuAnchor,
  onMenuOpen,
  onMenuClose,
}) => {
  const channel = getChannelMeta(whatsApp?.channel);
  const status = getStatusMeta(whatsApp?.status);
  const ChannelIcon = channel.Icon || Email;
  const statusLabel =
    i18n.t(`connections.status.${whatsApp.status}`) || whatsApp.status;

  return (
    <Card className={classes.connectionCard} variant="outlined">
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardHeader}>
          <Box display="flex" alignItems="center" style={{ gap: 12, minWidth: 0 }}>
            <Avatar
              className={classes.channelAvatar}
              style={{ backgroundColor: channel.bg, color: channel.color }}
            >
              <ChannelIcon fontSize="small" />
            </Avatar>
            <Box minWidth={0}>
              <Typography className={classes.cardTitle} noWrap>
                {whatsApp.name || channel.label || "Conexión"}
              </Typography>
              <Typography className={classes.cardId}>
                #{whatsApp.id} · {channel.label}
              </Typography>
            </Box>
          </Box>
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <IconButton size="small" onClick={(e) => onMenuOpen(e, whatsApp)}>
                <MoreVert fontSize="small" />
              </IconButton>
            )}
          />
        </Box>

        <Chip
          size="small"
          label={statusLabel.toUpperCase()}
          className={classes.statusBadge}
          style={{
            backgroundColor: status.bg,
            color: status.color,
          }}
          icon={
            <Box display="flex" alignItems="center" ml={0.5}>
              {renderStatusIcon(whatsApp)}
            </Box>
          }
        />

        <Typography className={classes.identifier}>
          {getConnectionIdentifier(whatsApp)}
        </Typography>

        {whatsApp.isDefault && (
          <Chip
            size="small"
            icon={<Star style={{ fontSize: 14 }} />}
            label={i18n.t("connections.card.default")}
            className={classes.defaultChip}
          />
        )}

        <Box className={classes.cardActions}>
          {renderActionButtons(whatsApp)}
        </Box>

        <Box className={classes.cardFooter}>
          <Typography className={classes.footerDate}>
            {i18n.t("connections.card.updated")}:{" "}
            {format(parseISO(whatsApp.updatedAt), "dd/MM/yyyy HH:mm")}
          </Typography>
        </Box>
      </CardContent>

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor?.el && menuAnchor?.id === whatsApp.id)}
        onClose={onMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            onEdit(whatsApp);
            onMenuClose();
          }}
        >
          <Edit fontSize="small" style={{ marginRight: 8 }} />
          {i18n.t("connections.card.edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(whatsApp.id);
            onMenuClose();
          }}
        >
          <DeleteOutline fontSize="small" style={{ marginRight: 8, color: "#DC2626" }} />
          {i18n.t("connections.card.delete")}
        </MenuItem>
      </Menu>
    </Card>
  );
};

const Connections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [addChannelModalOpen, setAddChannelModalOpen] = useState(false);
  const [zohoMailModalOpen, setZohoMailModalOpen] = useState(false);
  const [zohoEditConnection, setZohoEditConnection] = useState(null);
  const [syncingEmailId, setSyncingEmailId] = useState(null);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sortMode, setSortMode] = useState("default");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [integrationMenuAnchor, setIntegrationMenuAnchor] = useState(null);
  const [queueIntegrations, setQueueIntegrations] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [integrationsFetched, setIntegrationsFetched] = useState(false);
  const [queueIntegrationModalOpen, setQueueIntegrationModalOpen] = useState(false);
  const [selectedQueueIntegration, setSelectedQueueIntegration] = useState(null);
  const [webSearchIntegrationModalOpen, setWebSearchIntegrationModalOpen] = useState(false);
  const [selectedWebSearchIntegration, setSelectedWebSearchIntegration] = useState(null);
  const [chatAgentIntegrationModalOpen, setChatAgentIntegrationModalOpen] = useState(false);
  const [selectedChatAgentIntegration, setSelectedChatAgentIntegration] = useState(null);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    integrationId: null,
    promptId: null,
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [planConfig, setPlanConfig] = useState(false);

  const { user, socket } = useContext(AuthContext);
  const canUseFacebook = canUserAccessConnectionChannel(user, "facebook");
  const canUseInstagram = canUserAccessConnectionChannel(user, "instagram");
  const canUseWhatsapp = canUserAccessConnectionChannel(user, "whatsapp");
  const canUseEmail = canUserAccessConnectionChannel(user, "email");
  const canAddConnections =
    canUseFacebook || canUseInstagram || canUseWhatsapp || canUseEmail;

  const companyId = user.companyId;
  const { getPlanCompany } = usePlans();

  const sortedWhatsApps = useMemo(
    () => sortConnections(whatsApps || [], sortMode),
    [whatsApps, sortMode]
  );

  const canManageIntegrations = useMemo(
    () => hasAnyIntegrationAccess(planConfig) && user.profile !== "user",
    [planConfig, user.profile]
  );

  const canUseOpenAi = useMemo(
    () => Boolean(planConfig?.plan?.useOpenAi) && user.profile !== "user",
    [planConfig, user.profile]
  );

  const needsIntegrationFetch = useMemo(
    () =>
      Boolean(
        planConfig?.plan?.useIntegrations || planConfig?.plan?.useOpenAi
      ),
    [planConfig]
  );

  const isPageLoading = useMemo(
    () =>
      loading ||
      !planConfig?.plan ||
      (needsIntegrationFetch && !integrationsFetched),
    [loading, planConfig, needsIntegrationFetch, integrationsFetched]
  );

  const connectionItems = useMemo(() => {
    const channels = (sortedWhatsApps || []).map((whatsApp) => ({
      id: `channel-${whatsApp.id}`,
      kind: "channel",
      name: whatsApp.name,
      type: whatsApp.channel,
      status: whatsApp.status,
      data: whatsApp,
    }));

    const integrations = (queueIntegrations || []).map((integration) => ({
      id: `integration-${integration.id}`,
      kind: "integration",
      name: integration.name,
      type: integration.type,
      status: "ACTIVE",
      data: integration,
    }));

    const promptItems = (prompts || []).map((prompt) => ({
      id: `prompt-${prompt.id}`,
      kind: "prompt",
      name: prompt.name,
      type: "openai",
      status: "ACTIVE",
      data: prompt,
    }));

    return [...channels, ...integrations, ...promptItems];
  }, [sortedWhatsApps, queueIntegrations, prompts]);

  const sortedConnectionItems = useMemo(
    () => sortConnectionItems(connectionItems, sortMode),
    [connectionItems, sortMode]
  );

  const channelItems = useMemo(
    () => sortedConnectionItems.filter((item) => item.kind === "channel"),
    [sortedConnectionItems]
  );

  const integrationItems = useMemo(
    () => sortedConnectionItems.filter((item) => item.kind === "integration"),
    [sortedConnectionItems]
  );

  const promptItems = useMemo(
    () => sortedConnectionItems.filter((item) => item.kind === "prompt"),
    [sortedConnectionItems]
  );

  const categoryFilterOptions = useMemo(
    () => [
      {
        key: "all",
        label: i18n.t("connections.categoryFilters.all", { defaultValue: "Todos" }),
      },
      {
        key: "channels",
        label: i18n.t("connections.categoryFilters.channels", { defaultValue: "Canales" }),
      },
      {
        key: "integrations",
        label: i18n.t("connections.categoryFilters.integrations", {
          defaultValue: "Integraciones",
        }),
      },
      {
        key: "openai",
        label: i18n.t("connections.categoryFilters.openai", { defaultValue: "Open AI" }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const filterOptions = [
    { key: "default", label: i18n.t("connections.filters.default") },
    { key: "name", label: i18n.t("connections.filters.name") },
    { key: "type", label: i18n.t("connections.filters.type") },
    { key: "status", label: i18n.t("connections.filters.status") },
  ];

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIntegrationsData = useCallback(async () => {
    if (!planConfig?.plan) return;

    try {
      const requests = [];

      if (planConfig.plan.useIntegrations || planConfig.plan.useOpenAi) {
        requests.push(
          api
            .get("/queueIntegration/", { params: { searchParam: "", pageNumber: 1 } })
            .then(({ data }) => ({ type: "integrations", data: data.queueIntegrations || [] }))
        );
      }

      if (planConfig.plan.useOpenAi) {
        requests.push(
          api
            .get("/prompt")
            .then(({ data }) => ({ type: "prompts", data: data.prompts || [] }))
        );
      }

      const results = await Promise.all(requests);
      results.forEach((result) => {
        if (result.type === "integrations") {
          setQueueIntegrations(result.data);
        }
        if (result.type === "prompts") {
          setPrompts(result.data);
        }
      });
    } catch (err) {
      toastError(err);
    } finally {
      setIntegrationsFetched(true);
    }
  }, [planConfig]);

  useEffect(() => {
    fetchIntegrationsData();
  }, [fetchIntegrationsData]);

  useEffect(() => {
    if (!planConfig?.plan) {
      setIntegrationsFetched(false);
      return;
    }
    if (!needsIntegrationFetch) {
      setIntegrationsFetched(true);
    }
  }, [planConfig, needsIntegrationFetch]);

  useEffect(() => {
    if (!socket?.on || !companyId) return undefined;

    const onQueueIntegrationEvent = (data) => {
      if (data.action === "create" || data.action === "update") {
        setQueueIntegrations((prev) => {
          const index = prev.findIndex((item) => item.id === data.queueIntegration.id);
          if (index !== -1) {
            const next = [...prev];
            next[index] = data.queueIntegration;
            return next;
          }
          return [data.queueIntegration, ...prev];
        });
      }
      if (data.action === "delete") {
        setQueueIntegrations((prev) =>
          prev.filter((item) => item.id !== +data.integrationId)
        );
      }
    };

    const onPromptEvent = (data) => {
      if (data.action === "create" || data.action === "update") {
        setPrompts((prev) => {
          const index = prev.findIndex((item) => item.id === data.prompt.id);
          if (index !== -1) {
            const next = [...prev];
            next[index] = data.prompt;
            return next;
          }
          return [data.prompt, ...prev];
        });
      }
      if (data.action === "delete") {
        setPrompts((prev) => prev.filter((item) => item.id !== data.promptId));
      }
    };

    if (planConfig?.plan?.useIntegrations) {
      socket.on(`company-${companyId}-queueIntegration`, onQueueIntegrationEvent);
    }
    if (planConfig?.plan?.useOpenAi) {
      socket.on(`company-${companyId}-prompt`, onPromptEvent);
    }

    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueIntegrationEvent);
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket, companyId, planConfig]);

  const responseFacebook = (response) => {
    logFacebookOAuthDebug(response, {
      channel: "facebook",
      requestedScopes: FACEBOOK_OAUTH_SCOPES,
    });

    if (!response || response.status === "unknown" || response.error) {
      toast.info(
        i18n.t("connections.facebook.cancelled", {
          defaultValue: "Inicio de sesión de Facebook cancelado o incompleto",
        })
      );
      return;
    }

    const { accessToken, id } = response;
    if (!accessToken || !id) {
      toast.error(
        i18n.t("connections.facebook.missingToken", {
          defaultValue: "Facebook no devolvió un token válido",
        })
      );
      return;
    }

    api
      .post("/facebook", { facebookUserId: id, facebookUserToken: accessToken })
      .then(() => toast.success(i18n.t("connections.facebook.success")))
      .catch((error) => toastError(error));
  };

  const responseInstagram = (response) => {
    logFacebookOAuthDebug(response, {
      channel: "instagram",
      requestedScopes: INSTAGRAM_OAUTH_SCOPES,
    });

    if (!response || response.status === "unknown" || response.error) {
      toast.info(
        i18n.t("connections.facebook.cancelled", {
          defaultValue: "Inicio de sesión de Instagram cancelado o incompleto",
        })
      );
      return;
    }

    const { accessToken, id } = response;
    if (!accessToken || !id) {
      toast.error(
        i18n.t("connections.facebook.missingToken", {
          defaultValue: "Instagram no devolvió un token válido",
        })
      );
      return;
    }

    api
      .post("/facebook", {
        addInstagram: true,
        facebookUserId: id,
        facebookUserToken: accessToken,
      })
      .then(() => toast.success(i18n.t("connections.facebook.success")))
      .catch((error) => toastError(error));
  };

  useEffect(() => {
    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    });
  }, [whatsApps, history, socket, user.companyId]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenAddChannelModal = () => {
    setAddChannelModalOpen(true);
  };

  const handleCloseAddChannelModal = () => {
    setAddChannelModalOpen(false);
  };

  const handleOpenNewPrompt = () => {
    setSelectedPrompt(null);
    setPromptModalOpen(true);
  };

  const handleSelectIntegration = (integration) => {
    if (integration.id === "openai") {
      handleOpenNewPrompt();
      return;
    }

    if (integration.id === "tavily") {
      setSelectedWebSearchIntegration(null);
      setWebSearchIntegrationModalOpen(true);
      return;
    }

    if (integration.id === "chat_agent") {
      setSelectedChatAgentIntegration(null);
      setChatAgentIntegrationModalOpen(true);
      return;
    }

    setSelectedQueueIntegration({ type: integration.integrationType });
    setQueueIntegrationModalOpen(true);
  };

  const handleCloseWebSearchIntegrationModal = () => {
    setWebSearchIntegrationModalOpen(false);
    setSelectedWebSearchIntegration(null);
    fetchIntegrationsData();
  };

  const handleCloseChatAgentIntegrationModal = () => {
    setChatAgentIntegrationModalOpen(false);
    setSelectedChatAgentIntegration(null);
    fetchIntegrationsData();
  };

  const handleCloseQueueIntegrationModal = () => {
    setQueueIntegrationModalOpen(false);
    setSelectedQueueIntegration(null);
    fetchIntegrationsData();
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
    fetchIntegrationsData();
  };

  const handleEditIntegrationItem = (item) => {
    if (item.kind === "prompt") {
      setSelectedPrompt(item.raw);
      setPromptModalOpen(true);
      return;
    }

    if (item.raw?.type === "tavily") {
      setSelectedWebSearchIntegration(item.raw);
      setWebSearchIntegrationModalOpen(true);
      return;
    }

    if (item.raw?.type === "chat_agent") {
      setSelectedChatAgentIntegration(item.raw);
      setChatAgentIntegrationModalOpen(true);
      return;
    }

    setSelectedQueueIntegration(item.raw);
    setQueueIntegrationModalOpen(true);
  };

  const handleDeleteIntegrationItem = (item) => {
    if (item.kind === "prompt") {
      setConfirmModalInfo({
        action: "deletePrompt",
        title: i18n.t("connections.confirmationModal.deletePromptTitle"),
        message: i18n.t("connections.confirmationModal.deletePromptMessage"),
        promptId: item.raw.id,
      });
    } else {
      setConfirmModalInfo({
        action: "deleteIntegration",
        title: i18n.t("connections.confirmationModal.deleteIntegrationTitle"),
        message: i18n.t("connections.confirmationModal.deleteIntegrationMessage"),
        integrationId: item.raw.id,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSyncEmail = async (whatsAppId) => {
    setSyncingEmailId(whatsAppId);
    try {
      const { data } = await api.post(`/email/${whatsAppId}/sync`);
      toast.success(
        data?.processed
          ? `${data.processed} correo(s) sincronizado(s)`
          : i18n.t("connections.zohoModal.testSuccess")
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSyncingEmailId(null);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, []);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, []);

  const handleEditWhatsApp = (whatsApp) => {
    if (whatsApp.channel === "email") {
      setZohoEditConnection(whatsApp);
      setZohoMailModalOpen(true);
      return;
    }
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId,
      });
    }
    if (action === "delete") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "deleteIntegration") {
      try {
        await api.delete(`/queueIntegration/${confirmModalInfo.integrationId}`);
        toast.success(i18n.t("queueIntegration.toasts.deleted"));
        setQueueIntegrations((prev) =>
          prev.filter((item) => item.id !== confirmModalInfo.integrationId)
        );
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "deletePrompt") {
      try {
        const { data } = await api.delete(`/prompt/${confirmModalInfo.promptId}`);
        toast.info(i18n.t(data.message));
        setPrompts((prev) =>
          prev.filter((item) => item.id !== confirmModalInfo.promptId)
        );
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => handleOpenConfirmationModal("closedImported", whatsApp.id)}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      const isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime()
      );
      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages)
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              size="small"
              endIcon={<CircularProgress size={12} className={classes.buttonProgress} />}
              variant="outlined"
              color="primary"
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
    return null;
  };

  const renderActionButtons = (whatsApp) => {
    if (whatsApp.channel === "email") {
      if (whatsApp.status === "CONNECTED") {
        return (
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={syncingEmailId === whatsApp.id}
                onClick={() => handleSyncEmail(whatsApp.id)}
              >
                {syncingEmailId === whatsApp.id ? (
                  <CircularProgress size={18} />
                ) : (
                  i18n.t("connections.buttons.syncEmail")
                )}
              </Button>
            )}
          />
        );
      }
      return null;
    }

    if (whatsApp.status === "qrcode") {
      return (
        <Can
          role={getConnectionsPageRole(user)}
          perform="connections-page:addConnection"
          yes={() => (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleOpenQrModal(whatsApp)}
            >
              {i18n.t("connections.buttons.qrcode")}
            </Button>
          )}
        />
      );
    }

    if (whatsApp.status === "DISCONNECTED") {
      return (
        <Can
          role={getConnectionsPageRole(user)}
          perform="connections-page:addConnection"
          yes={() => (
            <>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleStartWhatsAppSession(whatsApp.id)}
              >
                {i18n.t("connections.buttons.tryAgain")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => handleRequestNewQrCode(whatsApp.id)}
              >
                {i18n.t("connections.buttons.newQr")}
              </Button>
            </>
          )}
        />
      );
    }

    if (["CONNECTED", "PAIRING", "TIMEOUT"].includes(whatsApp.status)) {
      return (
        <Can
          role={user.profile}
          perform="connections-page:addConnection"
          yes={() => (
            <>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
              >
                {i18n.t("connections.buttons.disconnect")}
              </Button>
              {renderImportButton(whatsApp)}
            </>
          )}
        />
      );
    }

    if (whatsApp.status === "OPENING") {
      return (
        <Button size="small" variant="outlined" disabled color="default">
          {i18n.t("connections.buttons.connecting")}
        </Button>
      );
    }

    return null;
  };

  const renderStatusIcon = (whatsApp) => {
    switch (whatsApp.status) {
      case "DISCONNECTED":
        return <SignalCellularConnectedNoInternet0Bar style={{ fontSize: 16, color: "#DC2626" }} />;
      case "OPENING":
        return <CircularProgress size={14} className={classes.buttonProgress} />;
      case "qrcode":
        return <CropFree style={{ fontSize: 16, color: "#2563EB" }} />;
      case "CONNECTED":
        return <SignalCellular4Bar style={{ fontSize: 16, color: "#059669" }} />;
      case "TIMEOUT":
      case "PAIRING":
        return <SignalCellularConnectedNoInternet2Bar style={{ fontSize: 16, color: "#D97706" }} />;
      default:
        return <CheckCircle style={{ fontSize: 16, color: theme.palette.text.secondary }} />;
    }
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  const renderFilterChip = (option, active, onClick) => (
    <Chip
      key={option.key}
      label={option.label}
      clickable
      size={isMobile ? "small" : "medium"}
      variant={active ? "default" : "outlined"}
      className={`${classes.filterChip} ${active ? classes.filterChipActive : ""}`}
      onClick={onClick}
    />
  );
  const renderNewConnectionButton = () => (
    <Can
      role={getConnectionsPageRole(user)}
      perform="connections-page:addConnection"
      yes={() => (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          className={classes.headerBtn}
          onClick={handleOpenAddChannelModal}
        >
          {i18n.t("connections.addChannel")}
        </Button>
      )}
    />
  );

  const renderNewConnectionIconButton = () => (
    <Can
      role={getConnectionsPageRole(user)}
      perform="connections-page:addConnection"
      yes={() => (
        <Tooltip title={i18n.t("connections.addChannel")}>
          <IconButton
            className={`${classes.headerIconBtn} ${classes.headerIconBtnPrimary}`}
            onClick={handleOpenAddChannelModal}
            size="small"
            aria-label={i18n.t("connections.addChannel")}
          >
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    />
  );

  const renderIntegrationCards = (items) =>
    items.map((item) => {
      const cardItem = mapIntegrationCardItem(item);
      return (
        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
          <IntegrationConnectionCard
            item={cardItem}
            classes={classes}
            user={user}
            menuAnchor={integrationMenuAnchor}
            onMenuOpen={(e, integrationItem) =>
              setIntegrationMenuAnchor({
                el: e.currentTarget,
                id: integrationItem.menuId,
              })
            }
            onMenuClose={() => setIntegrationMenuAnchor(null)}
            onEdit={() => handleEditIntegrationItem(cardItem)}
            onDelete={() => handleDeleteIntegrationItem(cardItem)}
          />
        </Grid>
      );
    });

  const renderChannelCards = (items) =>
    items.map((item) => {
      const whatsApp = item.data;
      return (
        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
          <ConnectionCard
            whatsApp={whatsApp}
            classes={classes}
            user={user}
            onEdit={handleEditWhatsApp}
            onDelete={(id) => handleOpenConfirmationModal("delete", id)}
            renderActionButtons={renderActionButtons}
            renderStatusIcon={renderStatusIcon}
            menuAnchor={cardMenuAnchor}
            onMenuOpen={(e, connection) =>
              setCardMenuAnchor({ el: e.currentTarget, id: connection.id })
            }
            onMenuClose={() => setCardMenuAnchor(null)}
          />
        </Grid>
      );
    });

  const renderConnectorSection = (title, subtitle, items, renderCards, emptyAction = null) => {
    if (!items.length && !emptyAction) return null;

    return (
      <Box className={classes.integrationsSection} mb={3}>
        <Box
          className={classes.integrationsSectionHeader}
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <Box>
            <Typography className={classes.integrationsSectionTitle}>
              {title}
            </Typography>
            {subtitle && (
              <Typography className={classes.integrationsSectionSubtitle}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {emptyAction}
        </Box>
        {items.length > 0 ? (
          <Grid container spacing={2}>
            {renderCards(items)}
          </Grid>
        ) : (
          <Box className={classes.emptyState} py={4}>
            <Typography variant="body1" gutterBottom>
              {i18n.t("connections.openAiSection.empty", {
                defaultValue: "Aún no tienes configuraciones Open AI",
              })}
            </Typography>
            {emptyAction}
          </Box>
        )}
      </Box>
    );
  };

  const showChannels =
    categoryFilter === "all" || categoryFilter === "channels";
  const showIntegrations =
    categoryFilter === "all" || categoryFilter === "integrations";
  const showOpenAi = categoryFilter === "all" || categoryFilter === "openai";

  const visibleCategoryFilters = categoryFilterOptions.filter((option) => {
    if (option.key === "openai" && !canUseOpenAi) return false;
    if (
      option.key === "integrations" &&
      !planConfig?.plan?.useIntegrations
    ) {
      return false;
    }
    return true;
  });

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <AddChannelModal
        open={addChannelModalOpen}
        onClose={handleCloseAddChannelModal}
        canUseWhatsapp={canUseWhatsapp}
        canUseFacebook={canUseFacebook}
        canUseInstagram={canUseInstagram}
        planConfig={planConfig}
        onSelectWhatsapp={handleOpenWhatsAppModal}
        onSelectZoho={() => {
          setZohoEditConnection(null);
          setZohoMailModalOpen(true);
        }}
        canUseEmail={canUseEmail}
        onFacebookConnect={responseFacebook}
        onInstagramConnect={responseInstagram}
        onSelectIntegration={handleSelectIntegration}
      />
      <ZohoMailModal
        open={zohoMailModalOpen}
        connection={zohoEditConnection}
        onClose={() => {
          setZohoMailModalOpen(false);
          setZohoEditConnection(null);
        }}
      />
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      <QueueIntegrationModal
        open={queueIntegrationModalOpen}
        onClose={handleCloseQueueIntegrationModal}
        integrationId={selectedQueueIntegration?.id}
        defaultType={selectedQueueIntegration?.id ? undefined : selectedQueueIntegration?.type}
      />
      <WebSearchIntegrationModal
        open={webSearchIntegrationModalOpen}
        onClose={handleCloseWebSearchIntegrationModal}
        integrationId={selectedWebSearchIntegration?.id}
      />
      <ChatAgentIntegrationModal
        open={chatAgentIntegrationModalOpen}
        onClose={handleCloseChatAgentIntegrationModal}
        integrationId={selectedChatAgentIntegration?.id}
      />
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      {user.profile === "user" && !canUserAccessConnectionsPage(user) ? (
        <ForbiddenPage />
      ) : (
        <>
          <Box className={classes.connectionsHeader}>
            <Box className={classes.pageHeader}>
              <Title>
                {i18n.t("connections.title")} ({connectionItems.length})
              </Title>
              <Typography className={classes.subtitle}>
                {i18n.t("connections.subtitle")}
              </Typography>
            </Box>
            <Box className={classes.headerActions}>
              {canUseWhatsapp && (
                <Box className={classes.headerActionItem}>
                  {isMobile ? (
                    <Tooltip title={i18n.t("connections.restartConnections")}>
                      <IconButton
                        className={classes.headerIconBtn}
                        onClick={restartWhatsapps}
                        size="small"
                        color="primary"
                        aria-label={i18n.t("connections.restartConnections")}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.headerBtn}
                      onClick={restartWhatsapps}
                    >
                      {i18n.t("connections.restartConnections")}
                    </Button>
                  )}
                </Box>
              )}
              {(canAddConnections || canManageIntegrations) && (
                <Box className={classes.headerActionItem}>
                  {isMobile ? renderNewConnectionIconButton() : renderNewConnectionButton()}
                </Box>
              )}
            </Box>
          </Box>

          <Box className={classes.filterBar}>
            {visibleCategoryFilters.map((option) =>
              renderFilterChip(
                option,
                categoryFilter === option.key,
                () => setCategoryFilter(option.key)
              )
            )}
          </Box>

          <Box className={classes.filterBar}>
            {filterOptions.map((option) =>
              renderFilterChip(
                option,
                sortMode === option.key,
                () => setSortMode(option.key)
              )
            )}
          </Box>

          <Box className={classes.filtersScroll}>
            {visibleCategoryFilters.map((option) =>
              renderFilterChip(
                option,
                categoryFilter === option.key,
                () => setCategoryFilter(option.key)
              )
            )}
            <Box className={classes.filterSeparator} aria-hidden />
            {filterOptions.map((option) =>
              renderFilterChip(
                option,
                sortMode === option.key,
                () => setSortMode(option.key)
              )
            )}
          </Box>

          {statusImport?.all ? (
            <Card className={classes.importCard} variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {statusImport?.this === -1
                    ? i18n.t("connections.buttons.preparing")
                    : i18n.t("connections.buttons.importing")}
                </Typography>
                {statusImport?.this === -1 ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" align="center" gutterBottom>
                      {`${i18n.t("connections.typography.processed")} ${statusImport?.this} ${i18n.t("connections.typography.in")} ${statusImport?.all} · ${i18n.t("connections.typography.date")}: ${statusImport?.date}`}
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <CircularProgressWithLabel
                        value={(statusImport?.this / statusImport?.all) * 100}
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Box className={classes.gridContainer}>
            {isPageLoading ? (
              <Box className={classes.skeletonGrid}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <Box key={n} className={classes.skeletonCard} />
                ))}
              </Box>
            ) : sortedConnectionItems.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>
                  {i18n.t("connections.empty")}
                </Typography>
                {(canAddConnections || canManageIntegrations) && renderNewConnectionButton()}
              </Box>
            ) : (
              <>
                {showChannels &&
                  (categoryFilter === "all" ? (
                    renderConnectorSection(
                      i18n.t("connections.addChannelModal.sections.channels"),
                      null,
                      channelItems,
                      renderChannelCards
                    )
                  ) : (
                    <Grid container spacing={2}>
                      {renderChannelCards(channelItems)}
                    </Grid>
                  ))}

                {showIntegrations &&
                  (categoryFilter === "all" ? (
                    renderConnectorSection(
                      i18n.t("connections.addChannelModal.sections.integrations"),
                      null,
                      integrationItems,
                      renderIntegrationCards
                    )
                  ) : (
                    <Grid container spacing={2}>
                      {renderIntegrationCards(integrationItems)}
                    </Grid>
                  ))}

                {showOpenAi &&
                  canUseOpenAi &&
                  renderConnectorSection(
                    `${i18n.t("connections.openAiSection.title", { defaultValue: "Open AI" })} (${promptItems.length})`,
                    i18n.t("connections.openAiSection.subtitle", {
                      defaultValue:
                        "Cada configuración es independiente: distinto prompt, modelo y API key",
                    }),
                    promptItems,
                    renderIntegrationCards,
                    null
                  )}
              </>
            )}
          </Box>
        </>
      )}
    </MainContainer>
  );
};

export default Connections;
